import { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { FileText, Upload, Trash2, Paperclip, X, Download } from 'lucide-react'
import { DocumentPreviewModal } from './DocumentPreviewModal'

type Document = {
  id: string
  name: string
  file_url?: string | null
  blob_signed_id?: string | null
  filename?: string | null
  created_at: string
}

type Props = {
  title: string
  documents: Document[]
  uploadPath: string
  deletePath: (docId: string) => string
}

function formatDate(date: string) {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentUploader({ title, documents, uploadPath, deletePath }: Props) {
  const [name, setName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const confirm = useConfirm()

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !selectedFile) return

    const formData = new FormData()
    formData.append('document[name]', name.trim())
    formData.append('document[file]', selectedFile)

    setUploading(true)
    router.post(uploadPath, formData, {
      forceFormData: true,
      onFinish: () => {
        setUploading(false)
        setName('')
        setSelectedFile(null)
        if (fileRef.current) fileRef.current.value = ''
      }
    })
  }

  const handleDelete = async (docId: string) => {
    if (!await confirm({ description: 'Remove this document?', confirmLabel: 'Remove', destructive: true })) return
    router.delete(deletePath(docId))
  }

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file)
    if (file && !name.trim()) {
      setName(file.name.replace(/\.[^/.]+$/, ''))
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const getFileUrl = (doc: Document) => {
    if (doc.file_url) return doc.file_url
    if (doc.blob_signed_id) return `/rails/active_storage/blobs/redirect/${doc.blob_signed_id}/${doc.filename || 'download'}`
    return null
  }

  return (
    <section className="border rounded-lg">
      <div className="px-4 py-3 border-b">
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
      </div>

      {/* Document list */}
      {documents.length > 0 && (
        <div className="divide-y">
          {documents.map(doc => {
            const url = getFileUrl(doc)
            return (
              <div key={doc.id} className="group flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{doc.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {doc.filename && <span>{doc.filename}</span>}
                      {doc.filename && doc.created_at && <span> · </span>}
                      {doc.created_at && <span>{formatDate(doc.created_at)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {url && (
                    <div className="flex items-center gap-1">
                      <DocumentPreviewModal url={url} filename={doc.filename || doc.name || 'Document'} />
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </a>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                    onClick={() => handleDelete(doc.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Upload area */}
      <div className={`px-4 py-4 ${documents.length > 0 ? 'border-t' : ''}`}>
        <form onSubmit={handleUpload} className="space-y-3">
          {/* Drop zone / file picker */}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
          />

          {!selectedFile ? (
            <div
              className={`border border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-foreground bg-muted/50' : 'border-border hover:border-muted-foreground hover:bg-muted/30'
              }`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="w-4 h-4 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Drop a file here or <span className="text-foreground font-medium">choose file</span>
              </p>
            </div>
          ) : (
            <div className="border rounded-lg px-3 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Paperclip className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground truncate">{selectedFile.name}</div>
                <div className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => { setSelectedFile(null); if (fileRef.current) fileRef.current.value = '' }}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          {/* Name + Upload button */}
          {selectedFile && (
            <div className="flex items-center gap-2">
              <Input
                placeholder="Document name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="h-8 text-sm flex-1"
              />
              <Button type="submit" size="sm" className="h-8 text-xs shrink-0" disabled={uploading || !name.trim()}>
                <Upload className="w-3 h-3 mr-1" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          )}
        </form>
      </div>
    </section>
  )
}
